import { useId, type ElementType, type ReactNode } from "react";
import { cx } from "../cx";

const OVERLINE = "text-xs font-semibold text-muted uppercase";

export function Overline({
  as: Tag = "h2",
  spacing = "wide",
  className,
  id,
  children,
}: {
  as?: ElementType;
  spacing?: "wide" | "narrow";
  className?: string;
  id?: string;
  children: ReactNode;
}) {
  return (
    <Tag id={id} className={cx(OVERLINE, spacing === "wide" ? "tracking-[0.06em]" : "tracking-[0.04em]", className)}>
      {children}
    </Tag>
  );
}

export function Panel({
  title,
  note,
  actions,
  id,
  children,
}: {
  title: string;
  note?: string;
  actions?: ReactNode;
  id?: string;
  children: ReactNode;
}) {
  const headingId = useId();
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className="min-w-0 rounded-xl border border-divider bg-bg px-4 pt-3 pb-3.5 max-narrow:px-3"
    >
      <div className="mb-2.5 flex min-h-7 flex-wrap items-center gap-2.5">
        <h2
          id={headingId}
          className="m-0 flex flex-wrap items-baseline gap-x-2 text-xs font-semibold tracking-[0.04em] text-muted uppercase"
        >
          {title}
          {note ? <span className="font-normal tracking-normal normal-case">{note}</span> : null}
        </h2>
        {actions ? <div className="ml-auto flex flex-wrap items-center gap-1.5">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-divider px-2.5 py-1.5">
      <small className="block truncate text-xs text-muted">{label}</small>
      <b className="font-mono text-xl font-semibold tabular-nums text-fg">{value}</b>
      <span className="block text-xs text-muted">{note}</span>
    </div>
  );
}
